import { ArrowDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { Plot } from '@/components/charts';
import { ErrorState } from '@/components/common/ErrorState';
import { DataPagination } from '@/components/pagination';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { KeyValueList } from '@/components/ui/key-value-list';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import {
  useAnalyticsSummary,
  useCarrierBreakdown,
  useScoreDistribution,
  useStatusFunnel,
  useTopAdvisors,
} from '@/hooks/api/useAnalytics';
import { useForwardsAnalytics } from '@/hooks/api/useForwardsAnalytics';
import {
  useForwardActivity,
  useObservabilitySummary,
  useOpportunityDismissals,
  useProductDismissals,
  type ForwardActivityRow,
  type ObservabilityListQuery,
  type OpportunityDismissalRow,
  type ProductDismissalRow,
} from '@/hooks/api/useObservability';
import { formatCurrencyCompact, formatDateTime, formatNumber, formatScore } from '@/lib/formatters';
import { cn } from '@/lib/utils';

import { ForwardsTabSkeleton, StatsSkeleton } from './StatsSkeleton';
import { CarrierBreakdownChart } from './sections/CarrierBreakdownChart';
import { ScoreDistributionChart } from './sections/ScoreDistributionChart';
import { StatusFunnelChart } from './sections/StatusFunnelChart';
import { TopAdvisorsTable } from './sections/TopAdvisorsTable';
import { TopRecommendedProductsChart } from './sections/TopRecommendedProductsChart';
import { TopReplacedCarriersChart } from './sections/TopReplacedCarriersChart';
import { baseLayout, CHART_COLORS } from './sections/chart-config';

type TabValue = 'opportunities' | 'forwards' | 'feedback' | 'activity';
type DetailRow = OpportunityDismissalRow | ProductDismissalRow | ForwardActivityRow;
type ForwardActivityPoint = { date: string; count: number };
interface AlgorithmNode {
  id: string;
  title: string;
  body: string;
  detail: string;
  stat?: string;
}

interface AlgorithmFlow {
  title: string;
  description: string;
  nodes: AlgorithmNode[];
}

const DEFAULT_OBSERVABILITY_FILTERS: ObservabilityListQuery = {
  page: 1,
  pageSize: 25,
};

const ACTIVITY_WINDOW_DAYS = 30;

export function StatsPage() {
  const [activeTab, setActiveTab] = useState<TabValue>('feedback');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const allTabs: { label: string; value: TabValue; adminOnly?: boolean }[] = [
    { label: 'Feedback', value: 'feedback', adminOnly: true },
    { label: 'Activity', value: 'activity', adminOnly: true },
    { label: 'Opportunities', value: 'opportunities' },
    { label: 'Replacements', value: 'forwards' },
  ];
  const tabs = allTabs.filter((tab) => !tab.adminOnly || isAdmin);
  const effectiveActiveTab = tabs.some((tab) => tab.value === activeTab)
    ? activeTab
    : (tabs[0]?.value ?? 'opportunities');
  const {
    data: forwardsData,
    isLoading: forwardsLoading,
    isError: forwardsIsError,
    error: forwardsError,
    refetch: forwardsRefetch,
  } = useForwardsAnalytics({ enabled: effectiveActiveTab === 'forwards' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <div
          className="inline-flex h-7 items-center rounded-md p-0.5"
          style={{ backgroundColor: 'hsl(var(--muted))' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'h-6 rounded px-2 text-xs font-medium transition-colors',
                effectiveActiveTab === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              style={
                effectiveActiveTab === tab.value
                  ? { backgroundColor: 'hsl(var(--background))' }
                  : undefined
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {effectiveActiveTab === 'feedback' && isAdmin ? (
        <FeedbackTab />
      ) : effectiveActiveTab === 'activity' && isAdmin ? (
        <ActivityTab />
      ) : effectiveActiveTab === 'opportunities' ? (
        <OpportunitiesTab enabled />
      ) : effectiveActiveTab === 'forwards' ? (
        <ForwardsTab
          data={forwardsData}
          isLoading={forwardsLoading}
          isError={forwardsIsError}
          error={forwardsError}
          refetch={forwardsRefetch}
        />
      ) : (
        <OpportunitiesTab enabled />
      )}
    </div>
  );
}

// ============================================================================
// Opportunities Tab (extracted from original StatsPage)
// ============================================================================

function OpportunitiesTab({ enabled }: { enabled: boolean }) {
  const summary = useAnalyticsSummary(undefined, { enabled });
  const scoreDistribution = useScoreDistribution(undefined, { enabled });
  const statusFunnel = useStatusFunnel(undefined, { enabled });
  const carrierBreakdown = useCarrierBreakdown(undefined, { enabled });
  const topAdvisors = useTopAdvisors(undefined, { enabled });
  const queries = [summary, scoreDistribution, statusFunnel, carrierBreakdown, topAdvisors];
  const isLoading = queries.some((query) => query.isLoading);
  const firstError = queries.find((query) => query.isError);

  if (isLoading) {
    return <StatsSkeleton />;
  }

  if (firstError) {
    return (
      <ErrorState
        message={
          firstError.error instanceof Error
            ? firstError.error.message
            : 'Failed to load analytics data'
        }
        onRetry={() => queries.forEach((query) => query.refetch())}
      />
    );
  }

  if (
    !summary.data ||
    !scoreDistribution.data ||
    !statusFunnel.data ||
    !carrierBreakdown.data ||
    !topAdvisors.data
  ) {
    return null;
  }

  const summaryData = summary.data;

  const scoreDistributionData = asArray(scoreDistribution.data);
  const statusFunnelData = asArray(statusFunnel.data);
  const carrierBreakdownData = asArray(carrierBreakdown.data);
  const topAdvisorsData = asArray(topAdvisors.data);

  const totalFromHistogram = scoreDistributionData.reduce((sum, b) => sum + b.count, 0);
  const highScoreCount = scoreDistributionData
    .filter((b) => b.min >= 80)
    .reduce((sum, b) => sum + b.count, 0);
  const highScorePct = totalFromHistogram > 0 ? (highScoreCount / totalFromHistogram) * 100 : 0;

  const summaryItems = [
    {
      label: 'Total Opportunities',
      value: <span className="font-tabular">{formatNumber(summaryData.totalOpportunities)}</span>,
    },
    {
      label: 'Total Est. Value',
      value: (
        <span className="font-tabular">
          {formatCurrencyCompact(summaryData.totalEstimatedValue)}
        </span>
      ),
    },
    {
      label: 'Avg Est. Value',
      value: (
        <span className="font-tabular">{formatCurrencyCompact(summaryData.avgEstimatedValue)}</span>
      ),
    },
    {
      label: 'Avg Score',
      value: <span className="font-tabular">{formatScore(summaryData.avgScore)}</span>,
    },
    {
      label: 'Avg Policy Age',
      value: <span className="font-tabular">{summaryData.avgPolicyAge.toFixed(1)}y</span>,
    },
    {
      label: 'Avg Account Value',
      value: (
        <span className="font-tabular">{formatCurrencyCompact(summaryData.avgAccountValue)}</span>
      ),
    },
    {
      label: 'High Score (80+)',
      value: <span className="font-tabular">{highScorePct.toFixed(1)}%</span>,
    },
  ];

  return (
    <div className="space-y-4 pt-2">
      {/* Top row: Score Distribution (left) + Summary Stats (right) */}
      <div className="grid gap-4 lg:grid-cols-[1fr,320px]">
        <ScoreDistributionChart data={scoreDistributionData} />
        <Card
          className="bg-gradient-card"
          style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
        >
          <CardHeader>
            <CardTitle className="text-sm">Summary Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <KeyValueList inline boldValues size="lg" items={summaryItems} />
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: Top Carriers (left) + Status Breakdown (right) */}
      <div className="grid gap-4 lg:grid-cols-2">
        <CarrierBreakdownChart data={carrierBreakdownData} />
        <StatusFunnelChart data={statusFunnelData} />
      </div>

      {/* Top Advisors table */}
      <TopAdvisorsTable data={topAdvisorsData} />
    </div>
  );
}

// ============================================================================
// Algorithm Tab
// ============================================================================

const OPPORTUNITY_ALGORITHM_FLOW: AlgorithmFlow = {
  title: 'Opportunity scoring algorithm',
  description: 'How current policies become scored replacement opportunities.',
  nodes: [
    {
      id: 'policy-data',
      title: 'Normalize policy data',
      body: 'Start with synced policy, owner, value, surrender, income, and carrier profile fields.',
      detail:
        'Compute policy age from issue date when needed, compute years until surrender from surrender date, derive surrender loss as (account value - surrender value) / account value, normalize owner age, and parse account value, premium, and income benefit values.',
    },
    {
      id: 'carrier-rules',
      title: 'Resolve carrier thresholds',
      body: 'Use carrier profile settings when available, then fall back to system defaults.',
      detail:
        'Set min in-force years, min account value, max owner age, and max surrender loss from carrier profile values when present; otherwise use 2 years, $25K, age 93, and 7.5%. Income increase thresholds prefer age-banded rules, then carrier profile values, then null.',
    },
    {
      id: 'track',
      title: 'Choose income or accumulation path',
      body: 'Income signals send the policy down the income path; otherwise it follows accumulation logic.',
      detail:
        'Select income path when any of these are true: income feature flag, income rider flag, non-null income benefit value, or active income status. If none are present, select accumulation path.',
    },
    {
      id: 'income-fit',
      title: 'Score income-track fit',
      body: 'Evaluate whether the current income policy has enough income data and meets carrier increase requirements.',
      detail:
        'If income benefit value is missing, return 40. Otherwise start at 85 when income is active or 80 when not active, subtract 10 when a minimum income increase requirement exists, add 5 when an income rider is present, then clamp to 0-100.',
      stat: '20% weight',
    },
    {
      id: 'accumulation-fit',
      title: 'Score accumulation fit',
      body: 'Prefer clean accumulation contracts and discount policies with active income or rider signals.',
      detail:
        'For accumulation path, return 60 when income is active, 75 when an income rider is present, and 90 when no income rider or active-income signal is present.',
      stat: '20% weight',
    },
    {
      id: 'score-rules',
      title: 'Evaluate active scoring rules',
      body: 'Each enabled rule returns a 0-100 raw score, multiplied by its configured weight.',
      detail:
        'Run every enabled rule. Defaults: in-force minimum returns 100/0 at 25%, path fit returns 100/35 at 20% if score >= 70, out of surrender returns 100/50 at 15%, surrender loss tolerance returns 100/25 at 15%, account value minimum returns 100/0 at 15%, and owner age suitability returns 100/40 at 10%.',
    },
    {
      id: 'hard-disqualifiers',
      title: 'Apply hard disqualifiers',
      body: 'Some conditions force the total score to 0 regardless of the weighted rule results.',
      detail:
        'Force the total score to 0 when the estimated surrender loss is 20% or greater, or when the policy is less than 2 years old (policy age is the difference between today and the effective/issue date). Disqualified policies can never surface as opportunities.',
    },
    {
      id: 'threshold',
      title: 'Compare to minimum score',
      body: 'The weighted rule total is rounded, clamped to 0-100, and compared to the active threshold.',
      detail:
        'Sum weighted rule scores, round the total, clamp it between 0 and 100, then override the total to 0 for any hard-disqualified policy, and compare the result to the active rules config minimum score threshold. Scores at or above the threshold qualify.',
    },
    {
      id: 'value',
      title: 'Estimate value and save opportunity',
      body: 'Qualified policies get an estimated opportunity value and stored score breakdown.',
      detail:
        'For qualifying policies, upsert the opportunity with propensity score, JSON factor breakdown, reason codes, estimated value, rules config id/version, and scored timestamp. For newly non-qualifying policies, soft-delete active opportunities.',
    },
  ],
};

const PRODUCT_RECOMMENDATION_FLOW: AlgorithmFlow = {
  title: 'Product recommendation algorithm',
  description: 'How replacement products are filtered, scored, and ranked.',
  nodes: [
    {
      id: 'current-policy',
      title: 'Load current policy context',
      body: 'Start from the selected opportunity and current policy profile.',
      detail:
        'Load the opportunity policy snapshot with current carrier, annuity type, jurisdiction, owner age, account value, surrender value, income benefit value, current cap rate, current participation rate, guaranteed/initial rates, and tax qualification.',
    },
    {
      id: 'candidate-query',
      title: 'Find eligible ARW products',
      body: 'Query active products that match product type, state, age, premium, and FireLight availability.',
      detail:
        'Query active ARW products joined to carriers and FireLight mappings. Filter by compatible annuity/product type, state availability, owner issue age limits, premium minimum/maximum limits, and category-specific carrier exclusions.',
    },
    {
      id: 'income-filter',
      title: 'Apply income requirements',
      body: 'If the current policy has income, replacement candidates must support income riders.',
      detail:
        'When the current policy has income, drop candidates without an income rider withdrawal benefit type. If rider quotes are returned and the current income benefit value is known, drop candidates whose quoted income amount is not greater than the current income benefit.',
    },
    {
      id: 'sp500-filter',
      title: 'Require S&P 500 cap rate',
      body: 'Keep only products with a usable S&P 500 point-to-point cap rate.',
      detail:
        'Inspect each candidate account list and keep only products where the S&P 500 annual point-to-point cap-rate extractor returns a cap rate greater than 0.',
    },
    {
      id: 'match-score',
      title: 'Calculate product match score',
      body: 'Blend product economics, carrier strength, bonus coverage, and surrender period.',
      detail:
        'Calculate match score as rate improvement * 40% + AM Best carrier strength * 20% + premium bonus coverage * 25% + surrender period score * 15%, rounded to an integer. If the carrier is in the configured boosted-carrier list, add the configured boost and cap at 100.',
    },
    {
      id: 'selection',
      title: 'Sort and enforce carrier diversity',
      body: 'Rank candidates by S&P 500 rate or match score, then apply unique-carrier selection rules.',
      detail:
        'If sort mode is S&P 500, sort by extracted S&P 500 cap rate descending; otherwise sort by match score descending. When unique carriers are required, greedily keep the first product per carrier until max matches is reached. When minimum unique carriers is configured, fill unique carriers first, then add best remaining products.',
    },
    {
      id: 'rate-comparison',
      title: 'Compare against current rates',
      body: 'Compute the best available rate improvement for each selected product.',
      detail:
        'Run rate comparison against current cap or participation rates when present. If a current rate and best improvement exist, set rate score to min(100, 50 + improvement percentage * 2) and recommendation score to match score * 60% + rate score * 40%.',
    },
    {
      id: 'recommendation-score',
      title: 'Rank final recommendations',
      body: 'Generate final recommendation scores, summaries, and product ordering.',
      detail:
        'If current rates are missing but a new rate exists, add min(15, new rate * 0.5) to match score. If no rate improvement exists, blend match score * 60% with a fixed rate score of 40. Cap final recommendation score at 100, generate the summary, and sort by recommendation score unless S&P 500 mode is selected.',
    },
  ],
};

export function AlgorithmTab() {
  const [activeAlgorithm, setActiveAlgorithm] = useState<'opportunity' | 'replacement'>(
    'opportunity'
  );
  const activeFlow =
    activeAlgorithm === 'opportunity' ? OPPORTUNITY_ALGORITHM_FLOW : PRODUCT_RECOMMENDATION_FLOW;

  return (
    <div className="space-y-4 pt-2">
      <div className="grid gap-3 lg:grid-cols-3">
        <AlgorithmSummaryCard
          label="Two engines"
          value="Opportunity + Product"
          detail="One engine decides what deserves attention; the other ranks replacement products."
        />
        <AlgorithmSummaryCard
          label="Weighted scoring"
          value="0-100 scores"
          detail="Each rule or product factor contributes a transparent weighted score."
        />
        <AlgorithmSummaryCard
          label="VP controls"
          value="Rules + feedback"
          detail="Carrier thresholds, dismissal feedback, and forwarded recommendations create the audit trail."
        />
      </div>

      <AlgorithmFlowDiagram
        flow={activeFlow}
        activeAlgorithm={activeAlgorithm}
        onAlgorithmChange={setActiveAlgorithm}
      />
    </div>
  );
}

function AlgorithmSummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <Card className="bg-gradient-card" style={{ boxShadow: 'var(--shadow-1)' }}>
      <CardContent className="p-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className="mt-1 text-lg font-semibold">{value}</p>
          <p className="mt-1 text-sm text-muted-foreground">{detail}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AlgorithmFlowDiagram({
  flow,
  activeAlgorithm,
  onAlgorithmChange,
}: {
  flow: AlgorithmFlow;
  activeAlgorithm: 'opportunity' | 'replacement';
  onAlgorithmChange: (value: 'opportunity' | 'replacement') => void;
}) {
  return (
    <Card className="overflow-hidden bg-gradient-card" style={{ boxShadow: 'var(--shadow-2)' }}>
      <CardHeader className="border-b border-border">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>{flow.title}</CardTitle>
            <CardDescription className="mt-1">{flow.description}</CardDescription>
          </div>
          <div
            className="inline-flex h-8 w-fit items-center rounded-md p-0.5"
            style={{ backgroundColor: 'hsl(var(--muted))' }}
          >
            {[
              { label: 'Opportunity', value: 'opportunity' as const },
              { label: 'Replacement', value: 'replacement' as const },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => onAlgorithmChange(tab.value)}
                className={cn(
                  'h-7 rounded px-3 text-xs font-medium transition-colors',
                  activeAlgorithm === tab.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                style={
                  activeAlgorithm === tab.value
                    ? { backgroundColor: 'hsl(var(--background))' }
                    : undefined
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-5">
          <div className="space-y-3">
            {flow.nodes.map((node, index) => (
              <AlgorithmNodeCard
                key={node.id}
                node={node}
                stepNumber={index + 1}
                showArrow={index < flow.nodes.length - 1}
              />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AlgorithmNodeCard({
  node,
  stepNumber,
  showArrow,
}: {
  node: AlgorithmNode;
  stepNumber: number;
  showArrow: boolean;
}) {
  return (
    <div className="relative grid grid-cols-[72px,1fr] gap-4">
      <div className="flex flex-col items-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-background text-sm font-semibold shadow-sm">
          {stepNumber}
        </div>
        {showArrow ? <div className="my-2 h-full min-h-8 w-px bg-border" /> : null}
        {showArrow ? (
          <div className="-mt-2 text-muted-foreground">
            <ArrowDown className="h-5 w-5" />
          </div>
        ) : null}
      </div>

      <div className="min-h-[156px] rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base font-semibold leading-tight">{node.title}</h3>
          {node.stat ? (
            <Badge variant="secondary" className="shrink-0">
              {node.stat}
            </Badge>
          ) : null}
        </div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{node.body}</p>
        <div className="mt-4 rounded-md border border-border bg-muted/40 p-3 text-xs leading-5 text-muted-foreground">
          {node.detail}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Forwards Tab (new)
// ============================================================================

function ForwardsTab({
  data,
  isLoading,
  isError,
  error,
  refetch,
}: {
  data: ReturnType<typeof useForwardsAnalytics>['data'];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}) {
  if (isLoading) {
    return <ForwardsTabSkeleton />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message || 'Failed to load forwards analytics'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data) return null;

  const summaryItems = [
    {
      label: 'Total Forwards',
      value: <span className="font-tabular">{formatNumber(data.summary.totalForwards)}</span>,
    },
    {
      label: 'Value Forwarded',
      value: (
        <span className="font-tabular">
          {formatCurrencyCompact(data.summary.totalAccountValueForwarded)}
        </span>
      ),
    },
    {
      label: 'This Week',
      value: <span className="font-tabular">{formatNumber(data.summary.forwardsThisWeek)}</span>,
    },
    {
      label: 'This Month',
      value: <span className="font-tabular">{formatNumber(data.summary.forwardsThisMonth)}</span>,
    },
  ];

  return (
    <div className="space-y-4 pt-2">
      {/* Top row: Recommended Products (left) + Replaced Carriers (right) */}
      <div className="grid gap-4 lg:grid-cols-2">
        <TopRecommendedProductsChart data={data.topRecommendedProducts} />
        <TopReplacedCarriersChart data={data.topReplacedCarriers} />
      </div>

      {/* Bottom row: Summary Stats (left) + Forwarded By (right) */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          className="bg-gradient-card"
          style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
        >
          <CardHeader>
            <CardTitle className="text-sm">Summary Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <KeyValueList inline boldValues size="lg" items={summaryItems} />
          </CardContent>
        </Card>
        <Card
          className="bg-gradient-card"
          style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
        >
          <CardHeader>
            <CardTitle className="text-sm">Forwarded By</CardTitle>
          </CardHeader>
          <CardContent>
            {data.summary.byForwarder.length > 0 ? (
              <KeyValueList
                inline
                boldValues
                size="lg"
                items={data.summary.byForwarder.map((f) => ({
                  label: f.forwardedBy || 'Unknown',
                  value: (
                    <span className="font-tabular">
                      {formatNumber(f.count)} · {formatCurrencyCompact(f.totalAccountValue)}
                    </span>
                  ),
                }))}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No forwards yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FeedbackTab() {
  const [filters, setFilters] = useState<ObservabilityListQuery>(DEFAULT_OBSERVABILITY_FILTERS);
  const [selectedRow, setSelectedRow] = useState<DetailRow | null>(null);
  const summary = useObservabilitySummary();
  const opportunityDismissals = useOpportunityDismissals(filters);
  const productDismissals = useProductDismissals(filters);

  return (
    <div className="space-y-4 pt-2">
      <FeedbackOverview data={summary.data} isLoading={summary.isLoading} />
      <ObservabilityFilters
        search={filters.search ?? ''}
        onSearchChange={(search) => setFilters((current) => ({ ...current, page: 1, search }))}
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <OpportunityDismissalsTable
          data={opportunityDismissals.data}
          isLoading={opportunityDismissals.isLoading}
          isError={opportunityDismissals.isError}
          onRetry={() => opportunityDismissals.refetch()}
          onRowClick={setSelectedRow}
          onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
          onPageSizeChange={(pageSize) =>
            setFilters((current) => ({ ...current, page: 1, pageSize }))
          }
        />
        <ProductDismissalsTable
          data={productDismissals.data}
          isLoading={productDismissals.isLoading}
          isError={productDismissals.isError}
          onRetry={() => productDismissals.refetch()}
          onRowClick={setSelectedRow}
          onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
          onPageSizeChange={(pageSize) =>
            setFilters((current) => ({ ...current, page: 1, pageSize }))
          }
        />
      </div>
      <ObservabilityDetailDialog
        row={selectedRow}
        onOpenChange={(open) => !open && setSelectedRow(null)}
      />
    </div>
  );
}

function ActivityTab() {
  const [filters, setFilters] = useState<ObservabilityListQuery>(DEFAULT_OBSERVABILITY_FILTERS);
  const [selectedRow, setSelectedRow] = useState<DetailRow | null>(null);
  const summary = useObservabilitySummary();
  const forwards = useForwardActivity(filters);

  return (
    <div className="space-y-4 pt-2">
      <ActivityOverTimeChart
        data={summary.data?.forwardsOverTime ?? []}
        isLoading={summary.isLoading}
        isError={summary.isError}
        onRetry={() => summary.refetch()}
      />
      <ObservabilityFilters
        search={filters.search ?? ''}
        onSearchChange={(search) => setFilters((current) => ({ ...current, page: 1, search }))}
      />
      <ForwardActivityTable
        data={forwards.data}
        isLoading={forwards.isLoading}
        isError={forwards.isError}
        onRetry={() => forwards.refetch()}
        onRowClick={setSelectedRow}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        onPageSizeChange={(pageSize) =>
          setFilters((current) => ({ ...current, page: 1, pageSize }))
        }
      />
      <ObservabilityDetailDialog
        row={selectedRow}
        onOpenChange={(open) => !open && setSelectedRow(null)}
      />
    </div>
  );
}

function ActivityOverTimeChart({
  data,
  isLoading,
  isError,
  onRetry,
}: {
  data: ForwardActivityPoint[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const series = buildActivitySeries(data);
  const total = series.reduce((sum, point) => sum + point.count, 0);
  const activeDays = series.filter((point) => point.count > 0).length;
  const bestDay = series.reduce((best, point) => (point.count > best.count ? point : best), {
    date: '',
    label: '—',
    count: 0,
  });
  const maxCount = Math.max(...series.map((point) => point.count), 0);

  const summaryItems = [
    { label: 'Last 30 Days', value: formatNumber(total) },
    { label: 'Active Days', value: formatNumber(activeDays) },
    {
      label: 'Best Day',
      value: total > 0 ? `${formatNumber(bestDay.count)} on ${bestDay.label}` : '—',
    },
  ];

  return (
    <Card
      className="bg-gradient-card"
      style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
    >
      <CardHeader className="gap-3 pb-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <CardTitle className="text-sm">Activity Over Time</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Forwarded recommendations by day across the last 30 days.
          </p>
        </div>
        <div className="grid gap-3 text-xs sm:grid-cols-3 lg:min-w-[420px]">
          {summaryItems.map((item) => (
            <div key={item.label}>
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {item.label}
              </p>
              <p className="mt-1 font-tabular text-sm font-semibold">{item.value}</p>
            </div>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isError ? (
          <ErrorState message="Failed to load recommendation activity" onRetry={onRetry} />
        ) : isLoading ? (
          <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
            Loading activity...
          </div>
        ) : (
          <Plot
            data={[
              {
                type: 'scatter',
                mode: 'lines+markers',
                x: series.map((point) => point.date),
                y: series.map((point) => point.count),
                customdata: series.map((point) => point.label),
                line: { color: CHART_COLORS.info, width: 2.5 },
                marker: {
                  size: series.map((point) => (point.count > 0 ? 7 : 4)),
                  color: series.map((point) =>
                    point.count > 0 ? CHART_COLORS.primary : CHART_COLORS.muted
                  ),
                  line: { color: 'rgba(255,255,255,0.7)', width: 1 },
                },
                fill: 'tozeroy',
                fillcolor: 'rgba(104,158,188,0.12)',
                hovertemplate: '%{customdata}<br>Forwarded recommendations: %{y}<extra></extra>',
              },
            ]}
            layout={baseLayout({
              margin: { l: 54, r: 24, t: 8, b: 42 },
              xaxis: {
                title: { text: 'Day' },
                type: 'date',
                tickformat: '%b %-d',
                showgrid: false,
                zeroline: false,
              },
              yaxis: {
                title: { text: 'Recommendations' },
                rangemode: 'tozero',
                dtick: maxCount <= 6 ? 1 : undefined,
                zeroline: false,
              },
              hoverlabel: {
                bgcolor: 'hsl(var(--popover))',
                bordercolor: 'hsl(var(--border))',
                font: { color: 'hsl(var(--popover-foreground))' },
              },
            })}
            config={{ displayModeBar: false, responsive: true }}
            useResizeHandler
            className="h-72 w-full"
          />
        )}
      </CardContent>
    </Card>
  );
}

function FeedbackOverview({
  data,
  isLoading,
}: {
  data: ReturnType<typeof useObservabilitySummary>['data'];
  isLoading: boolean;
}) {
  const totals = data?.totals;
  const summaryItems = [
    {
      label: 'Opportunity Dismissals',
      value: (
        <span className="font-tabular">
          {isLoading ? '...' : formatNumber(totals?.opportunityDismissals)}
        </span>
      ),
    },
    {
      label: 'Product Feedback',
      value: (
        <span className="font-tabular">
          {isLoading ? '...' : formatNumber(totals?.productDismissals)}
        </span>
      ),
    },
    {
      label: 'Forwards',
      value: (
        <span className="font-tabular">{isLoading ? '...' : formatNumber(totals?.forwards)}</span>
      ),
    },
    {
      label: 'Forward Rate',
      value: (
        <span className="font-tabular">
          {isLoading ? '...' : formatPercent(totals?.forwardRate)}
        </span>
      ),
    },
    {
      label: 'Opportunity Dismissal Rate',
      value: (
        <span className="font-tabular">
          {isLoading ? '...' : formatPercent(totals?.opportunityDismissalRate)}
        </span>
      ),
    },
  ];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr,1fr]">
      <Card className="bg-gradient-card" style={{ boxShadow: 'var(--shadow-2)' }}>
        <CardHeader>
          <CardTitle className="text-sm">Feedback Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <KeyValueList inline boldValues size="lg" items={summaryItems} />
        </CardContent>
      </Card>
      <Card className="bg-gradient-card" style={{ boxShadow: 'var(--shadow-2)' }}>
        <CardHeader>
          <CardTitle className="text-sm">Top Dismissal Signals</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <SimpleRankedList
            title="Products"
            rows={(data?.topDismissedProducts ?? []).map((row) => ({
              label: `${row.carrierName} - ${row.productName}`,
              value: row.count,
            }))}
          />
          <SimpleRankedList
            title="Carriers"
            rows={(data?.topDismissedCarriers ?? []).map((row) => ({
              label: row.carrierName,
              value: row.count,
            }))}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ObservabilityFilters({
  search,
  onSearchChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
}) {
  return (
    <Card className="border-border/70 bg-surface-2" style={{ boxShadow: 'var(--shadow-1)' }}>
      <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium">Admin activity filters</p>
          <p className="text-xs text-muted-foreground">
            Search comments, policy numbers, products, carriers, teams, and users.
          </p>
        </div>
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search activity..."
          className="max-w-sm"
        />
      </CardContent>
    </Card>
  );
}

function OpportunityDismissalsTable({
  data,
  isLoading,
  isError,
  onRetry,
  onRowClick,
  onPageChange,
  onPageSizeChange,
}: ObservabilityTableProps<OpportunityDismissalRow>) {
  return (
    <ObservabilityTableCard
      title="Opportunity Dismissals"
      description="Dismissed replacement opportunities and user comments."
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      columns={['Date', 'Policy', 'Current Product', 'Duration', 'Comment']}
      renderRow={(row) => (
        <tr
          key={row.id}
          className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
          onClick={() => onRowClick(row)}
        >
          <td className="px-3 py-2 text-muted-foreground">{formatDateTime(row.dismissedAt)}</td>
          <td className="px-3 py-2">
            <OpportunityLink id={row.opportunityId} label={row.policyNumber ?? 'View'} />
          </td>
          <td className="px-3 py-2">
            <TruncatedText value={joinParts([row.carrierName, row.productName])} />
          </td>
          <td className="px-3 py-2">
            <Badge variant="secondary" className="text-[10px]">
              {formatDurationLabel(row.duration)}
            </Badge>
          </td>
          <td className="px-3 py-2 text-muted-foreground">
            <TruncatedText value={row.comment} />
          </td>
        </tr>
      )}
    />
  );
}

function ProductDismissalsTable({
  data,
  isLoading,
  isError,
  onRetry,
  onRowClick,
  onPageChange,
  onPageSizeChange,
}: ObservabilityTableProps<ProductDismissalRow>) {
  return (
    <ObservabilityTableCard
      title="Product Feedback"
      description="Dismissed recommendation products and replacement context."
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      columns={['Date', 'Policy', 'Dismissed Product', 'Replacement', 'Comment']}
      renderRow={(row) => (
        <tr
          key={row.id}
          className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
          onClick={() => onRowClick(row)}
        >
          <td className="px-3 py-2 text-muted-foreground">{formatDateTime(row.dismissedAt)}</td>
          <td className="px-3 py-2">
            <OpportunityLink id={row.opportunityId} label={row.policyNumber ?? 'View'} />
          </td>
          <td className="px-3 py-2">
            <TruncatedText value={`${row.carrierName} - ${row.productName}`} />
          </td>
          <td className="px-3 py-2">
            <TruncatedText
              value={joinParts([row.replacementCarrierName, row.replacementProductName])}
            />
          </td>
          <td className="px-3 py-2 text-muted-foreground">
            <TruncatedText value={row.comment} />
          </td>
        </tr>
      )}
    />
  );
}

function ForwardActivityTable({
  data,
  isLoading,
  isError,
  onRetry,
  onRowClick,
  onPageChange,
  onPageSizeChange,
}: ObservabilityTableProps<ForwardActivityRow>) {
  return (
    <ObservabilityTableCard
      title="Forwarded Activity"
      description="Recommendation packages forwarded to the agent platform."
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      columns={[
        'Date',
        'Forwarded By',
        'Advisor',
        'Policy',
        'Replaced Product',
        'Forwarded Products',
        'Signals',
      ]}
      renderRow={(row) => (
        <tr
          key={row.id}
          className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
          onClick={() => onRowClick(row)}
        >
          <td className="px-3 py-2 text-muted-foreground">{formatDateTime(row.forwardedAt)}</td>
          <td className="px-3 py-2">
            <TruncatedText value={row.forwardedByEmail ?? 'Unknown'} />
          </td>
          <td className="px-3 py-2">{row.advisorName ?? 'Unknown'}</td>
          <td className="px-3 py-2">
            <OpportunityLink id={row.opportunityId} label={row.policyNumber ?? 'View'} />
          </td>
          <td className="px-3 py-2">
            <TruncatedText value={joinParts([row.currentCarrier, row.currentProduct])} />
          </td>
          <td className="px-3 py-2">
            <span className="font-tabular">{formatNumber(row.productCount)}</span>
          </td>
          <td className="px-3 py-2">
            <div className="flex flex-wrap gap-1">
              {row.summaryEdited && (
                <Badge variant="secondary" className="text-[10px]">
                  Summary edited
                </Badge>
              )}
              {row.productsReordered && (
                <Badge variant="secondary" className="text-[10px]">
                  Reordered
                </Badge>
              )}
            </div>
          </td>
        </tr>
      )}
      wide
    />
  );
}

interface ObservabilityTableProps<T extends DetailRow> {
  data?: { rows: T[]; page: number; pageSize: number; total: number };
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onRowClick: (row: T) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function ObservabilityTableCard<T extends DetailRow>({
  title,
  description,
  columns,
  data,
  isLoading,
  isError,
  renderRow,
  onRetry,
  onPageChange,
  onPageSizeChange,
  wide = false,
}: {
  title: string;
  description: string;
  columns: string[];
  data?: { rows: T[]; page: number; pageSize: number; total: number };
  isLoading: boolean;
  isError: boolean;
  renderRow: (row: T) => ReactNode;
  onRetry: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  wide?: boolean;
}) {
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.pageSize ?? 25)));

  return (
    <Card className={cn('border-border/70 bg-surface-2', wide && 'xl:col-span-2')}>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent className="p-0">
        {isError ? (
          <div className="p-4">
            <ErrorState message={`Failed to load ${title.toLowerCase()}`} onRetry={onRetry} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-y bg-primary/[0.04]">
                    {columns.map((column) => (
                      <th
                        key={column}
                        className="whitespace-nowrap px-3 py-2 text-left font-medium text-muted-foreground"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index} className="border-b">
                        {columns.map((column) => (
                          <td key={column} className="px-3 py-3 text-muted-foreground">
                            Loading...
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : data?.rows.length ? (
                    data.rows.map(renderRow)
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-3 py-10 text-center text-muted-foreground"
                      >
                        No activity found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {data && (
              <DataPagination
                page={data.page}
                pageSize={data.pageSize}
                totalCount={data.total}
                totalPages={totalPages}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ObservabilityDetailDialog({
  row,
  onOpenChange,
}: {
  row: DetailRow | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={Boolean(row)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-border bg-surface-0 text-foreground sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{row ? getDetailTitle(row) : 'Activity detail'}</DialogTitle>
          <DialogDescription>Full audit context for the selected activity row.</DialogDescription>
        </DialogHeader>
        {row && <DetailContent row={row} />}
      </DialogContent>
    </Dialog>
  );
}

function DetailContent({ row }: { row: DetailRow }) {
  if (isForwardRow(row)) {
    return (
      <div className="space-y-4">
        <DetailGrid
          items={[
            ['Forwarded at', formatDateTime(row.forwardedAt)],
            ['Forwarded by', row.forwardedByEmail ?? 'Unknown'],
            ['Advisor', row.advisorName ?? 'Unknown'],
            ['Policy number', row.policyNumber ?? '—'],
            ['Replaced product', joinParts([row.currentCarrier, row.currentProduct])],
            ['Account value', formatCurrencyCompact(row.currentAccountValue)],
            ['Summary edited', row.summaryEdited ? 'Yes' : 'No'],
            ['Products reordered', row.productsReordered ? 'Yes' : 'No'],
          ]}
        />
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Forwarded products</h3>
          <div className="rounded-md border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-primary/[0.04]">
                  <th className="px-3 py-2 text-left">Position</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-left">Score</th>
                  <th className="px-3 py-2 text-left">Firelight</th>
                </tr>
              </thead>
              <tbody>
                {row.forwardedProducts.map((product) => (
                  <tr
                    key={`${product.productId ?? product.productName}-${product.position}`}
                    className="border-b last:border-0"
                  >
                    <td className="px-3 py-2 font-tabular">
                      {product.position}
                      {product.originalPosition != null &&
                      product.originalPosition !== product.position
                        ? ` (was ${product.originalPosition})`
                        : ''}
                    </td>
                    <td className="px-3 py-2">
                      {product.carrierName} - {product.productName}
                    </td>
                    <td className="px-3 py-2 font-tabular">
                      {product.recommendationScore == null
                        ? '—'
                        : product.recommendationScore.toFixed(1)}
                    </td>
                    <td className="px-3 py-2">{product.firelightReady ? 'Ready' : 'Missing'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (isProductDismissalRow(row)) {
    return (
      <div className="space-y-4">
        <DetailGrid
          items={[
            ['Dismissed at', formatDateTime(row.dismissedAt)],
            ['Dismissed by', row.dismissedByEmail ?? 'Unknown'],
            ['Policy number', row.policyNumber ?? '—'],
            ['Dismissed product', `${row.carrierName} - ${row.productName}`],
            [
              'Recommendation score',
              row.recommendationScore == null ? '—' : row.recommendationScore.toFixed(1),
            ],
            ['Replacement', joinParts([row.replacementCarrierName, row.replacementProductName])],
            ['Team', row.salesTeamName ?? row.salesTeamId ?? '—'],
          ]}
        />
        <CommentBlock comment={row.comment} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DetailGrid
        items={[
          ['Dismissed at', formatDateTime(row.dismissedAt)],
          ['Dismissed by', row.dismissedByEmail ?? row.dismissedByName ?? 'Unknown'],
          ['Policy number', row.policyNumber ?? '—'],
          ['Current product', joinParts([row.carrierName, row.productName])],
          [
            'Opportunity score',
            row.opportunityScore == null ? '—' : row.opportunityScore.toFixed(0),
          ],
          ['Duration', formatDurationLabel(row.duration)],
          ['Dismissed until', formatDateTime(row.dismissedUntil)],
          ['Team', row.salesTeamName ?? row.salesTeamId ?? '—'],
        ]}
      />
      <CommentBlock comment={row.comment} />
    </div>
  );
}

function DetailGrid({ items }: { items: Array<[string, string]> }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-md border bg-surface-2 p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className="mt-1 text-sm">{value || '—'}</p>
        </div>
      ))}
    </div>
  );
}

function CommentBlock({ comment }: { comment: string }) {
  return (
    <div className="rounded-md border bg-surface-2 p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Comment
      </p>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-6">{comment}</p>
    </div>
  );
}

function SimpleRankedList({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; value: number }>;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-medium text-muted-foreground">{title}</p>
      {rows.length > 0 ? (
        <div className="space-y-2">
          {rows.slice(0, 5).map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-3 text-sm">
              <TruncatedTooltip text={row.label} />
              <span className="font-tabular text-muted-foreground">{formatNumber(row.value)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No data yet</p>
      )}
    </div>
  );
}

function OpportunityLink({ id, label }: { id: string; label: string }) {
  return (
    <Link
      to={`/opportunities/${id}`}
      className="font-medium text-primary underline-offset-2 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      <TruncatedTooltip text={label} className="max-w-[180px]" />
    </Link>
  );
}

function TruncatedText({ value }: { value: string }) {
  return <TruncatedTooltip text={value || '—'} className="max-w-[260px]" />;
}

function getDetailTitle(row: DetailRow): string {
  if (isForwardRow(row)) return 'Forwarded recommendation';
  if (isProductDismissalRow(row)) return 'Product dismissal feedback';
  return 'Opportunity dismissal feedback';
}

function isForwardRow(row: DetailRow): row is ForwardActivityRow {
  return 'forwardedAt' in row;
}

function isProductDismissalRow(row: DetailRow): row is ProductDismissalRow {
  return 'recommendationScore' in row && 'replacementProductName' in row;
}

function joinParts(parts: Array<string | null | undefined>): string {
  const filtered = parts.filter((part): part is string => Boolean(part));
  return filtered.length > 0 ? filtered.join(' - ') : '—';
}

function formatDurationLabel(duration: string): string {
  switch (duration) {
    case '3_months':
      return '3 months';
    case '6_months':
      return '6 months';
    case '1_year':
      return '1 year';
    case 'never':
      return 'Never';
    default:
      return duration;
  }
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function buildActivitySeries(
  data: ForwardActivityPoint[]
): Array<ForwardActivityPoint & { label: string }> {
  const countsByDate = new Map(data.map((point) => [point.date, point.count]));
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (ACTIVITY_WINDOW_DAYS - 1));

  return Array.from({ length: ACTIVITY_WINDOW_DAYS }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const dateKey = toLocalDateKey(date);
    return {
      date: dateKey,
      label: formatActivityDateLabel(dateKey),
      count: countsByDate.get(dateKey) ?? 0,
    };
  });
}

function toLocalDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatActivityDateLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return dateKey;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function asArray<T>(value: T[] | undefined): T[] {
  return Array.isArray(value) ? value : [];
}
